import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './ImageLightbox.css';

/**
 * Minimal in-app image viewer.
 *
 * Props:
 *   - open: boolean — whether the lightbox is visible
 *   - images: string[] — list of image URLs in the same message
 *   - startIndex: number — which image to show when opened
 *   - onClose: () => void — called on backdrop / Esc / close-button click
 *
 * Behavior:
 *   - Esc / backdrop / close-button → onClose
 *   - ArrowLeft/Right or on-screen ‹ › → cycle (wraps both ways)
 *   - When `images.length <= 1`, navigation buttons are hidden
 *   - Locks body scroll while open, restores on close/unmount
 *   - Rendered into document.body via Portal so it sits above modals
 */
const ImageLightbox = ({ open, images, startIndex = 0, onClose }) => {
  const total = images?.length || 0;
  const [index, setIndex] = useState(startIndex);

  // Sync index when the lightbox opens with a new startIndex.
  useEffect(() => {
    if (open) setIndex(Math.max(0, Math.min(startIndex, total - 1)));
  }, [open, startIndex, total]);

  const next = useCallback(() => {
    if (total <= 1) return;
    setIndex(i => (i + 1) % total);
  }, [total]);

  const prev = useCallback(() => {
    if (total <= 1) return;
    setIndex(i => (i - 1 + total) % total);
  }, [total]);

  // Keyboard shortcuts.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose, next, prev]);

  // Body scroll lock.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open || total === 0) return null;

  const safeIndex = Math.max(0, Math.min(index, total - 1));
  const current = images[safeIndex];
  const hasNav = total > 1;

  const stop = (e) => e.stopPropagation();

  const overlay = (
    <div
      className="lightbox-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
      onClick={onClose}
    >
      <div className="lightbox-content" onClick={stop}>
        <img
          src={current}
          alt={`Attachment ${safeIndex + 1} of ${total}`}
          className="lightbox-image"
        />

        <button
          type="button"
          className="lightbox-close"
          onClick={onClose}
          aria-label="Close image preview"
        >
          ×
        </button>

        {hasNav && (
          <>
            <button
              type="button"
              className="lightbox-nav lightbox-nav--prev"
              onClick={prev}
              aria-label="Previous image"
            >
              ‹
            </button>
            <button
              type="button"
              className="lightbox-nav lightbox-nav--next"
              onClick={next}
              aria-label="Next image"
            >
              ›
            </button>
            <div className="lightbox-counter" aria-hidden="true">
              {safeIndex + 1} / {total}
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Portal so the overlay isn't trapped inside any positioned ancestor.
  return createPortal(overlay, document.body);
};

export default ImageLightbox;
